import { useEffect, useState } from "react";

import { useLanguage } from "../i18n";
import { useLogger } from "../logger";
import { fetchLocations, type Location } from "../locationsApi";
import type { Specimen } from "../types";
import type { CareEvent } from "../eventsApi";
import { occurredAt, WhenRow } from "./WhenRow";

/**
 * Where a plant lives, and moving it.
 *
 * This is kept out of "How it is doing" deliberately. Condition and Soil
 * Quality are corrections to current state — you set them to whatever is true
 * now. A Move is something that happened on a date: it writes a Care Event, it
 * carries an undo window, and it can be backdated. Mixing the two would teach
 * the wrong model of what the app is recording.
 *
 * Logging goes through the same path as every other Care Event, so the move
 * inherits the eight-second grace, the undo bar and the pending state with no
 * new mechanism. Nothing is written during the window, so an undone move never
 * happened at all.
 */
export function WhereItLives({
  plant,
  events,
  warned,
}: {
  plant: Specimen;
  /** The plant's own history, so acceptance is derived the way the engine derives it. */
  events: CareEvent[];
  /** True when the plan is currently warning that the guide cannot schedule it here. */
  warned: boolean;
}) {
  const { t, pick } = useLanguage();
  const { log, pendingFor } = useLogger();
  const [locations, setLocations] = useState<Location[]>([]);
  // A move already in the grace window: the section reads as moved even though
  // nothing has been written yet — including which Location's acceptance
  // applies, which is the one it is going to.
  const moving = pendingFor(plant.id).find((entry) => entry.type === "move");
  const movingTo = (moving?.payload.to as { id?: string } | undefined)?.id;
  const currentId = movingTo ?? plant.location.id;
  // You notice a plant is in the wrong room days after carrying it there, and
  // it matters beyond tidiness: a Move discards the drying times measured
  // before it, so when it happened decides which ones survive.
  const [whenIndex, setWhenIndex] = useState(0);
  const [other, setOther] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetchLocations(controller.signal)
      .then(setLocations)
      .catch(() => {
        // The section simply does not offer a move. Everything else on the
        // page still works, which is the right failure for a rare action.
      });
    return () => controller.abort();
  }, []);

  /*
    Accepted here, read off the same history the engine reads. Scoped to the
    Location rather than to the plant, so carrying it elsewhere stops it
    applying and bringing it back makes it apply again — there is nothing to
    clear, on either side.
  */
  const acceptanceHere = events
    .filter(
      (event) =>
        event.type === "placement_accepted" &&
        event.payload.location_id === currentId,
    )
    // The list arrives newest first, so the head is the one in force — the same
    // rule the engine applies, appending rather than editing.
    .at(0);
  const acceptedHere = acceptanceHere !== undefined;
  const ownInterval = acceptanceHere?.payload.interval_days as number | undefined;

  // The guide cannot schedule it where it is. Either the plan is still saying
  // so, or it has stopped saying so because the owner already accepted it.
  const unschedulable = warned || acceptedHere;

  /*
    Accepting, optionally with the owner's own Check Interval.

    The presets are the values his own matrix actually uses, so the keyboard
    should almost never appear — but "almost" is why the free field stays. No
    number is a valid answer: he is not made to guess at the moment he knows
    least, and the plant simply stays unscheduled.
  */
  function accept(days?: number) {
    log(
      plant.id,
      "placement_accepted",
      { location_id: currentId, interval_days: days },
      days
        ? `${pick(plant.species.name)} — ${t.everyNDays(days)}`
        : `${pick(plant.species.name)} — ${t.keepingItHere}`,
    );
    setOther("");
  }



  function move(to: Location) {
    const from = locations.find((location) => location.id === plant.location.id);
    log(
      plant.id,
      "move",
      {
        // Both sides in full, so the history reads without a join long after
        // the fact — and both Placements, because that is what makes a move
        // mean anything to the schedule.
        from: from && {
          id: from.id, ar: from.name_ar, en: from.name_en, placement: from.placement,
        },
        to: {
          id: to.id, ar: to.name_ar, en: to.name_en, placement: to.placement,
        },
      },
      `${pick(plant.species.name)} — ${t.movedTo} ${pick({ ar: to.name_ar, en: to.name_en })}`,
      occurredAt(whenIndex),
    );
  }

  return (
    <section className="state-editor">
      <h2>{t.whereItLives}</h2>

      {locations.length > 1 && (
      <div className="button-row">
        {locations.map((location) => (
          <button
            key={location.id}
            type="button"
            className="action"
            aria-pressed={currentId === location.id}
            disabled={currentId === location.id}
            onClick={() => move(location)}
          >
            <span lang="ar">{location.name_ar}</span>
            <span className="action-note" dir="ltr">
              {location.name_en} · {location.placement === "outdoor" ? t.outdoor : t.indoor}
            </span>
          </button>
        ))}
      </div>
      )}

      {locations.length > 1 && <WhenRow index={whenIndex} onChange={setWhenIndex} />}

      <p className="state-note">{t.whereItLivesNote}</p>

      {/*
        The guide has no interval for this Species here, so the app can compute
        nothing about the plant. Accepting says "I know" and stops the warning;
        it deliberately does not pretend to schedule anything.
      */}
      {unschedulable && (
        <div className="accept-placement">
          <p className="accept-rule">{t.guideCannotSchedule}</p>
          {acceptedHere && (
            <p className="accept-state">
              {ownInterval ? t.ownIntervalNote(ownInterval) : t.keepingItHereNote}
            </p>
          )}

          {/* Offered whether or not the placement is already accepted: a bad
              first guess should not be permanent, and changing it is simply
              appending another acceptance. */}
          <p className="accept-ask">{acceptedHere ? t.changeTheInterval : t.howOftenToCheck}</p>
          <div className="chips">
            {[3, 5, 7, 10, 14, 21].map((days) => (
              <button
                key={days}
                type="button"
                className="chip when"
                aria-pressed={ownInterval === days}
                onClick={() => accept(days)}
              >
                {t.nDays(days)}
              </button>
            ))}
          </div>

          <div className="chips accept-other">
            <input
              type="number"
              min={1}
              max={365}
              inputMode="numeric"
              value={other}
              onChange={(event) => setOther(event.target.value)}
              placeholder={t.otherDays}
              aria-label={t.otherDays}
            />
            <button
              type="button"
              className="chip"
              disabled={!Number.isInteger(Number(other)) || Number(other) < 1}
              onClick={() => accept(Number(other))}
            >
              {t.use}
            </button>
            {!acceptedHere && (
              <button type="button" className="chip" onClick={() => accept()}>
                {t.keepItHereNoNumber}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
